import React, { useEffect, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  Byline,
  ExhibitionContainer,
  Paragraph,
  SectionTitle,
  PageTitle,
  HeroImage,
  FeatureImage,
  HeroCaption,
  FeatureCaption,
  Image,
  ImageCaption,
  Gallery,
  GalleryItem,
  MasonryGallery,
  MasonryItem,
  MasonryImage
} from '../../styles/exhibitionStyles';

export default function ExhibitionLondon() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const openLightbox = (imagePath) => {
    setCurrentImage(imagePath);
    setLightboxOpen(true);
  };

  // Masonry gallery images
  const masonryImages = [
    "/img/show-london/sleepr-show-8.jpg",
    "/img/show-london/sleepr-show-3.jpg",
    "/img/show-london/sleepr-show-41.jpg",
    "/img/show-london/sleepr-show-13.jpg",
    "/img/show-london/sleepr-show-57.jpg",
    "/img/show-london/sleepr-show-33.jpg",
    "/img/show-london/sleepr-show-17.jpg",
    "/img/show-london/sleepr-show-46.jpg",
    "/img/show-london/sleepr-show-5.jpg",
    "/img/show-london/sleepr-show-77.jpg",
    "/img/show-london/sleepr-show-59.jpg",
    "/img/show-london/sleepr-show-45.jpg",
  ];

  useEffect(() => {
    document.title = "Solo Exhibition: London, 2024 - SLEEPR";
  }, []);

  return (
    <ExhibitionContainer>
      {/* Hero Image */}
      <div onClick={() => openLightbox("/img/show-london/feature2.jpg")}>
        <HeroImage src="/img/show-london/feature2.jpg" alt="London Exhibition" />
      </div>
      <HeroCaption>Above: Sleepr made a presence at the gallery in Soho, London.</HeroCaption>

      <PageTitle>Secrets: Solo Exhibition, London (2024)</PageTitle>
      <Byline>In May 2024, Sleepr debuted his first solo exhibition with a limited series of works after posting online anonymously for more than 13 years. The exhibition featured large-scale installations, performance art and digital pieces that explore themes of secrecy, identity, and the digital age.
      </Byline>

      <SectionTitle>Secrets in the heart of London</SectionTitle>
      <Paragraph>
      The Secrets exhibition, held in May 2024 in Soho, London, presented a compelling new chapter in the oeuvre of the enigmatic artist known as Sleepr. This location, celebrated as the beating heart of creativity, served as the perfect backdrop for Sleepr's exploration of hidden worlds and surreal spaces. 
      </Paragraph>
      <Paragraph>The exhibition, highly anticipated within the art community, showcased 12 large-scale, museum quality framed works that measure an impressive 200 x 140 cm (approximately 79 x 55 inches)that invited viewers into Sleepr's vivid and complex dreamscapes.</Paragraph>

      {/* Feature Image */}
      <div onClick={() => openLightbox("/img/show-london/sleepr-show-25.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-25.jpg" alt="Exhibition Space" />
      </div>

      <SectionTitle>Critical acclaim and public reception</SectionTitle>
      <Paragraph>
      The Secrets exhibition received high praise from critics, who lauded Sleepr's ability to navigate complex themes with both subtlety and grandeur. The vibrant technicolor palette and the intricate composition of each piece were frequently highlighted as markers of Sleepr's exceptional skill. 
      </Paragraph>
      <Paragraph>The public reception was equally enthusiastic, with the gallery buzzing with visitors throughout the exhibition's run. The overwhelming support from the audience and critics alike underscores Sleepr's growing reputation as a leading voice in contemporary art.</Paragraph>

      {/* Masonry Gallery */}
      <SectionTitle>Exhibition Documentation</SectionTitle>
      <MasonryGallery>
        {masonryImages.map((image, index) => (
          <MasonryItem key={index} onClick={() => openLightbox(image)}>
            <MasonryImage src={image} alt={`Exhibition view ${index + 1}`} />
          </MasonryItem>
        ))}
      </MasonryGallery>

      {/* Other Feature Images */}
      <div onClick={() => openLightbox("/img/show-london/sleepr-show-87.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-87.jpg" alt="Exhibition Preview" />
      </div>

      <SectionTitle>The Work Of A Naturalist: Documenting The Supernatural</SectionTitle>
      <Paragraph>
      Sleepr's work, though highly innovative, is deeply rooted in the tradition of naturalist documentation. However, his approach diverges from the typical faithful reconstruction of the natural world. Instead, Sleepr's landscapes and scenes are infused with a hallucinatory quality, reflective of his ongoing exploration of altered states of consciousness.
      </Paragraph>
      <Paragraph>
      His work speaks to the experimental spirit of an artist who traverses both the natural and the supernatural, documenting not just what is seen, but what is felt and experienced beyond the ordinary realm.
      </Paragraph>

      <a href="\files\sleepr-secrets-catalogue-full.pdf">
        <FeatureImage src="/img/show-london/pdf-document-secrets.jpg" alt="Installation Detail" />
      </a>
      <FeatureCaption><a href="\files\sleepr-secrets-catalogue-full.pdf">Download the full Secrets catalogue (PDF) 38mb</a></FeatureCaption>

      <SectionTitle>The Secrets Catalogue</SectionTitle>
      <Paragraph>
      Accompanying the Secrets exhibition, an 80-page catalogue was meticulously designed, printed, and bound, embodying the same attention to detail that defines Sleepr's work. This luxurious publication includes four critical essays, evocative poems, and in-depth analyses of each individual artwork, providing a comprehensive exploration of the exhibition's themes. Unlike the standard A4 printed pages typically distributed at galleries, every patron who entered the exhibition received this exceptional catalogue, a testament to the gallery's commitment to delivering a measure of quality that enhances the overall experience and deepens the engagement with Sleepr's visionary art.
      </Paragraph>

      <div onClick={() => openLightbox("/img/show-london/sleepr-show-56.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-56.jpg" alt="Installation Detail" />
      </div>
      <div onClick={() => openLightbox("/img/show-london/sleepr-show-29.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-29.jpg" alt="Installation Detail" />
      </div>
      <SectionTitle>An Underground Movement Made Visible</SectionTitle>
      <Paragraph>
      Sleepr operates as a clandestine researcher, navigating the perilous and often illegal realms of the underground. His work illuminates an invisible spectrum of visions, revealing hidden dimensions that challenge conventional perceptions. By capturing these extraordinary realities, Sleepr not only brings to light the unseen but also amplifies the voices of artists and individuals who continue to resist the societal and governmental mechanisms that seek to control and suppress the use of psychedelics. Through his art, the struggle for cognitive freedom becomes visible, offering a powerful counter-narrative to the forces of regulation and repression.
      </Paragraph>

      <div onClick={() => openLightbox("/img/show-london/feature1.jpg")}>
        <FeatureImage src="/img/show-london/feature1.jpg" alt="Installation Detail" />
      </div>
      <SectionTitle>Documenting A Secret World: The Twelve Works</SectionTitle>
      <Paragraph>
      The thematic exploration of Secrets spans a vast array of dichotomies—historic and modern, urban and natural, two-dimensional and three-dimensional. Sleepr deftly navigates these intersections, revealing the concealed facets of reality that exist just beyond the surface. The exhibition's title itself suggests a journey into the unknown, where the viewer is invited to uncover hidden truths and encounter the sublime. This conceptual richness is further heightened by Sleepr's physical presence during the exhibition, integrating performance art into the narrative, and making the experience both intimate and expansive.
      </Paragraph>

      {/* The Twelve Works Gallery */}
      <Gallery>
        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_104.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_104.jpg" alt="The Bedroom Explorer" />
          <ImageCaption>The Bedroom Explorer</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_100.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_100.jpg" alt="An Exotic Trade" />
          <ImageCaption>An Exotic Trade</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_111.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_111.jpg" alt="The Underworld" />
          <ImageCaption>The Underworld</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_101.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_101.jpg" alt="Life, Death and Rebirth" />
          <ImageCaption>Life, Death and Rebirth</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_102.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_102.jpg" alt="It's All In Your Head" />
          <ImageCaption>It's All In Your Head</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_103.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_103.jpg" alt="Plant Spirits" />
          <ImageCaption>Plant Spirits</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_105.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_105.jpg" alt="The Harmala Mysteries" />
          <ImageCaption>The Harmala Mysteries</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_106.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_106.jpg" alt="The Hidden Circus" />
          <ImageCaption>The Hidden Circus</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_109.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_109.jpg" alt="The Source Of Novelty" />
          <ImageCaption>The Source Of Novelty</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_107.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_107.jpg" alt="The Magician's Chest" />
          <ImageCaption>The Magician's Chest</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_108.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_108.jpg" alt="The New God" />
          <ImageCaption>The New God</ImageCaption>
        </GalleryItem>

        <GalleryItem onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_110.jpg")}>
          <Image src="/img/artworks-1920/sleepr_1920_110.jpg" alt="The Surrender" />
          <ImageCaption>The Surrender</ImageCaption>
        </GalleryItem>
      </Gallery>

      <div onClick={() => openLightbox("/img/show-london/sleepr-show-35.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-35.jpg" alt="Installation Detail" />
      </div>
      <SectionTitle>The Surreal World of Hyperspace</SectionTitle>
      <Paragraph>
      Sleepr's works are a marvel of contemporary artistic practice, each imbued with vibrant technicolor and a filmic composition that blurs the line between the real and the fantastical. The dreamlike scenes are meticulously constructed, showcasing a mastery over both traditional and digital mediums.
      </Paragraph>
      <Paragraph>
      The works evoke a sense of otherworldliness, drawing viewers into a liminal space that is both recognizable and alien. Sleepr's technique, influenced by the precision of naturalist documentation, creates spaces that, while grounded in the natural world, remain unfamiliar and surreal.
      </Paragraph>

      <div onClick={() => openLightbox("/img/show-london/sleepr-show-4.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-4.jpg" alt="Installation Detail" />
      </div>
      <SectionTitle>Whispers and Buzz: The New Guard</SectionTitle>
      <Paragraph>
      The commercial success of Secrets cannot be overstated. 9 out of 12 works were pre-sold before the exhibition's opening is an almost unheard-of achievement in the art market, reflecting the high demand and anticipation for Sleepr's creations. To add to this, all works were sold sight unseen, and before the reveal.
      </Paragraph>
      <Paragraph>
      There was a decision to hold back three works from sale (The Hidden Circus, The New God, The Underworld), positioning these pieces as rare and highly coveted. Including one of the most sought after during the show's exhibition: The Underworld which had at five full-priced offers made on the piece, but none accepted.
      </Paragraph>
      <Paragraph>
      This market response positions Sleepr as an exciting and influential figure, signaling a shift in how contemporary art is valued and consumed. 
      </Paragraph>

      <div onClick={() => openLightbox("/img/show-london/sleepr-show-52.jpg")}>
        <FeatureImage src="/img/show-london/sleepr-show-52.jpg" alt="Installation Detail" />
      </div>
      <SectionTitle>An Emerging Figure In The Ultracontemporary Art World</SectionTitle>
      <Paragraph>
      Sleepr's Secrets exhibition not only solidifies his status as a emerging figure in the ultracontemporary fine art world, but also represents the emergence of a "new guard" who are unafraid to push boundaries and explore the unknown. Wrestling with identity, tech innovation, illegality, biohacking, the supernatural - there are few artists as exciting as what Sleepr is doing. Just listen to the artist speak and you'll hear the passion, drive and vision behind the work - all decisions are meticulous and planned.
      </Paragraph>
      <Paragraph>
      His work is a testament to the power of art to reveal hidden truths and to connect with audiences on a profound level. As Sleepr continues to evolve and experiment, his impact on the art world is poised to grow, ensuring that his work will resonate for years to come. Secrets may have been the first show, but it certainly won't be the last, with an exciting trajectory ahead.
      </Paragraph>

      {/* Lightbox - simple version with no navigation */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={[{ src: currentImage }]}
        carousel={{ finite: true }}
        render={{ 
          buttonPrev: () => null,
          buttonNext: () => null
        }}
      />
    </ExhibitionContainer>
  );
}
